import { GetMainMenuItemsParams, GridApi, GridOptions, ITooltipParams, MenuItemDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { autoinject, bindable, customElement, observable } from 'aurelia-framework';
import { AgGridState } from '../../classes/agGridState';
import { InstrumentValuesReportViewModel, InstrumentWithValuesViewModel, OverviewClient, OverviewRequest } from '@dynamik/common/clients/reporting-clients';
import { AvailableInstrumentClient as LookupAvailableInstrumentClient, LookupExchangeResponseItem } from '@dynamik/common/clients/instrument-clients';
import { ReportingOverviewContextMenu } from './reporting-overview-context-menu';
import { combineLatestWith, debounceTime, delayWhen, filter, ReplaySubject, Subject, switchMap, takeUntil, tap } from '../../rxjs-local';
import { CustomerService, InstrumentTypesCache, PortfolioReportCache } from '@dynamik/common';
import { CategoriesSettingsViewModel, CustomerSettingsClient, InstrumentTypeViewModel, IOverViewRequestViewModel, OverViewRequestViewModel, OverviewClient as TransactionOverviewClient } from '@dynamik/common/clients/transaction-clients';
import { getExcelStyles } from '../../resources/agGrid/excel-styles';
import { IAggregatedField, IOverviewAggregationComponentState, OverviewAggregationComponent } from '../../agElements/overview-aggregation-component/overview-aggregation-component';
import { AgGridAurelia } from 'insight/src/ag-grid-aurelia';
import { MdcSnackbarService } from '@aurelia-mdc-web/snackbar';
import { AlertService } from '@aurelia-toolkit/alert';

@autoinject
@customElement('reporting-overview')
export class ReportingOverview {
  constructor(private contextMenu: ReportingOverviewContextMenu, private agGridState: AgGridState, private customerService: CustomerService, private snackbarService: MdcSnackbarService,
    private overviewClient: OverviewClient, private transactionOverviewClient: TransactionOverviewClient, private instrumentTypesCache: InstrumentTypesCache, private alertService: AlertService,
    private lookupAvailableInstrumentClient: LookupAvailableInstrumentClient, private portfolioReportCache: PortfolioReportCache, private customerSettingsClient: CustomerSettingsClient) {
  }
  private instrumentTypes: InstrumentTypeViewModel[];
  private exchanges = new Map<string, LookupExchangeResponseItem>();
  private exchangesRequest: Promise<LookupExchangeResponseItem[]>;
  private exchangesError = false;
  private periodValues = new Map<string, InstrumentValuesReportViewModel>();
  private periodValuesRequest: Promise<InstrumentValuesReportViewModel[]>;
  private totalsCheck = 0;

  showAll$ = new ReplaySubject<boolean>(1);
  portfolio$ = new ReplaySubject<string>(1);

  @bindable
  stateKey: string = 'reporting-overview-v3';

  @observable
  showAll: boolean = false;
  showAllChanged() {
    this.showAll$.next(this.showAll);
  }

  @bindable
  gridStyle: string = 'height: 100%';

  @observable
  portfolio: string = '';
  portfolioChanged() {
    this.portfolio$.next(this.portfolio);
  }

  grid: AgGridAurelia;
  gridApi: GridApi<InstrumentWithValuesViewModel>;
  error: Error;
  useAverageCostPrice: boolean;
  categoryColumns: { colId: string; header: string; valueGetter: (params: ValueGetterParams<InstrumentWithValuesViewModel>) => string }[] = [];
  statusPanelState: IOverviewAggregationComponentState = { fields: [], allowSelection: true };
  nonAvgAggregatedFields: IAggregatedField[] = [
    { name: 'totalCostPrice', show: true, defaultShow: true, header: 'Cost Price' },
    { name: 'estimatedValue', show: true, defaultShow: true, header: 'Estimated Value' },
    { name: 'unrealized', show: true, defaultShow: true, header: 'Unrealized' },
    { name: 'taxValue', show: false, defaultShow: false, header: 'Tax value' },
    { name: 'accountingValue', show: false, defaultShow: false, header: 'Accounting value' },
    { name: 'unrealizedAccountingValue', show: false, defaultShow: false, header: 'Unreal. Acc. value' }
  ];
  avgAggregatedFields: IAggregatedField[] = [
    { name: 'avgCostPrice', show: true, defaultShow: true, header: 'Cost Price (AVG)' },
    { name: 'estimatedValue', show: true, defaultShow: true, header: 'Estimated Value' },
    { name: 'avgUnrealized', show: true, defaultShow: true, header: 'Unrealized' },
    { name: 'taxValue', show: false, defaultShow: false, header: 'Tax value' },
    { name: 'avgAccountingValue', show: false, defaultShow: false, header: 'Accounting value (AVG)' },
    { name: 'avgUnrealizedAccountingValue', show: false, defaultShow: false, header: 'Unreal. Acc. value (AVG)' }
  ];

  gridOptions: GridOptions<InstrumentWithValuesViewModel> = {
    defaultColDef: {
      filter: 'agTextColumnFilter',
      sortable: true,
      resizable: true
    },
    sideBar: false,
    enableAdvancedFilter: true,
    includeHiddenColumnsInAdvancedFilter: true,
    getContextMenuItems: params => this.contextMenu.getContextMenuItems(params, this),
    excelStyles: getExcelStyles(),
    rowModelType: 'clientSide',
    rowSelection: { mode: 'singleRow', checkboxes: false },
    paginationAutoPageSize: true,
    pagination: true,
    groupDefaultExpanded: -1,
    groupTotalRow: 'bottom',
    groupHideOpenParents: false,
    animateRows: false,
    debug: false,
    scrollbarWidth: 11,
    components: { OverviewAggregationComponent },
    statusBar: {
      statusPanels: [
        { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
        { statusPanel: 'OverviewAggregationComponent', statusPanelParams: { state: this.statusPanelState } }
      ]
    },
    getMainMenuItems: x => this.getMainMenuItems(x),
    onColumnMoved: async x => this.agGridState.saveGridStateDebounced(this.gridApi, this.stateKey, x.source),
    onColumnVisible: async x => this.agGridState.saveGridStateDebounced(this.gridApi, this.stateKey, x.source),
    onColumnResized: async x => this.agGridState.saveGridStateDebounced(this.gridApi, this.stateKey, x.source),
    onFilterChanged: async x => this.agGridState.saveGridStateDebounced(this.gridApi, this.stateKey, x.source),
    onSortChanged: async x => this.agGridState.saveGridStateDebounced(this.gridApi, this.stateKey, x.source),
    onDisplayedColumnsChanged: x => {
      if (x.source !== 'gridInitializing') {
        this.agGridState.saveGridStateDebounced(this.gridApi, this.stateKey, x.source);
      }
    },
    getRowId: x => `${x.data.portfolio}_${x.data.id}`,
    onGridReady: async params => {
      this.gridApi = params.api;
      await this.gridReady();
    }
  };

  gridReady$ = new ReplaySubject<void>(1);
  destroy$ = new Subject<void>();
  filterSub$ = this.customerService.customerAndDates$
    .pipe(
      filter(x => !!x.dates.to),
      delayWhen(() => this.gridReady$),
      combineLatestWith(this.showAll$, this.portfolio$),
      debounceTime(500),
      tap(x => {
        this.error = undefined;
        this.gridApi.setGridOption('loading', true);
      }),
      switchMap(async ([filter, showAll, portfolio]) => {
        const request: IOverViewRequestViewModel = { showAll: showAll, toDate: filter.dates.toStr, portfolio: portfolio };
        const [reporting, categories] = await Promise.all([
          this.overviewClient.get(filter.customer.companyId, new OverviewRequest(request)).catch(e => e as Error),
          this.customerSettingsClient.get(filter.customer.companyId).then(x => x.instrumentCategories).catch(() => [] as CategoriesSettingsViewModel[])
        ]);
        return {
          reporting,
          categories,
          identityId: filter.customer.identityId,
          companyId: filter.customer.companyId,
          fromDate: filter.dates.fromStr,
          request
        };
      }),
      takeUntil(this.destroy$)
    )
    .subscribe(async x => {
      this.gridApi.setGridOption('loading', false)
      this.useAverageCostPrice = this.customerService.selectedCustomer.useAverageCostPrice;
      this.categoryColumns = x.categories.filter(c => c.enabled).map(c => ({
        colId: `category${c.catTypeId}`,
        header: c.catTypeName,
        valueGetter: params => params.data?.categories?.find(y => y.securitiesCategoryType?.id === c.catTypeId)?.name
      }));
      await this.grid.updateColumns();
      this.agGridState.restoreGridState(this.gridApi, this.stateKey);
      this.loadAggregatedFields();
      if (x.reporting instanceof Error) {
        this.error = x.reporting;
        return;
      }
      this.gridApi.setGridOption('rowData', x.reporting.rows);
      void this.loadExchanges(x.reporting.rows);
      void this.loadPeriodValues(x.companyId, x.fromDate, x.request);
      void this.checkTransactionTotals(x.identityId, x.request, x.reporting.rows);
    });

  async activate(params: { stateKey: string; gridStyle: string }) {
    this.stateKey = params.stateKey;
    if (params.gridStyle) {
      this.gridStyle = params.gridStyle;
    }
  }

  async gridReady() {
    this.instrumentTypes = await this.instrumentTypesCache.instrumentTypes;
    // this.gridApi.sizeColumnsToFit();
    this.gridReady$.next();
  }

  loadAggregatedFields() {
    this.statusPanelState.fields = this.useAverageCostPrice ? this.avgAggregatedFields : this.nonAvgAggregatedFields;
    const key = `reporting-overview-aggregated-fields-${this.customerService.selectedCustomer.companyId}`;
    if (localStorage.getItem(key)) {
      const fields = JSON.parse(localStorage.getItem(key));
      this.statusPanelState.fields.forEach(x => {
        x.show = fields?.find(y => y.name === x.name)?.show ?? x.show;
      });
    }
  }

  detached() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getMainMenuItems(params: GetMainMenuItemsParams): (string | MenuItemDef)[] {
    const field = this.statusPanelState.fields.find(x => x.name === params.column.getColDef().field);
    if (!field) {
      return params.defaultItems;
    }

    const items: (string | MenuItemDef)[] = [...params.defaultItems, 'separator', {
      name: 'Aggregate',
      checked: field.show,
      action: () => {
        field.show = !field.show;
        this.saveAggregatedFields();
      }
    }];
    return items;
  }

  // The Transaction overview only cross-checks the cost price totals, so it runs after the rows are shown; an unreachable Transaction API skips the check.
  private async checkTransactionTotals(identityId: number, request: IOverViewRequestViewModel, rows: InstrumentWithValuesViewModel[]) {
    const check = ++this.totalsCheck;
    let transactionRows: { totalCostPrice?: number }[];
    try {
      transactionRows = (await this.transactionOverviewClient.instruments(identityId, new OverViewRequestViewModel(request))).rows;
    } catch {
      return;
    }
    if (check !== this.totalsCheck) {
      return;
    }
    const sum = (items: { totalCostPrice?: number }[]) => items.reduce((total, curr) => total + (curr.totalCostPrice ?? 0), 0);
    if (Math.abs(sum(rows) - sum(transactionRows)) <= 0.01) {
      return;
    }
    if (await this.alertService.alert({
      caption: 'Updating data',
      html: 'We\'re preparing your data in the background. This may take a moment.<br>To continue, please click Refresh data to load the latest information.<br>If the data is still not updated within a short time, please contact our support team at <a href="mailto:support@dynamik.no">support@dynamik.no.</a>',
      button2Text: 'Refresh data',
      showCloseButton: true
    } as any)) {
      window.location.reload();
    }
  }

  // The exchange of a noted instrument lives in InstrumentLookup; it is fetched after the rows so the overview never waits for it.
  private async loadExchanges(rows: InstrumentWithValuesViewModel[]) {
    this.exchanges = new Map();
    this.exchangesError = false;
    const ids = [...new Set(rows.map(x => x.lookupAvailableInstrumentId).filter((x): x is string => !!x))];
    if (ids.length === 0) {
      return;
    }
    const request = this.lookupAvailableInstrumentClient.exchanges(this.customerService.selectedCustomer.companyId, ids);
    this.exchangesRequest = request;
    try {
      const exchanges = await request;
      if (this.exchangesRequest !== request) {
        return;
      }
      this.exchanges = new Map(exchanges.map(x => [x.lookupAvailableInstrumentId, x]));
    } catch {
      if (this.exchangesRequest !== request) {
        return;
      }
      this.exchangesError = true;
    }
    this.gridApi.refreshClientSideRowModel('filter');
    this.gridApi.refreshCells({ columns: ['market', 'exchangeName'], force: true });
  }

  // Profit/Loss, Dividend and Interest for the selected period come from the portfolio report; they are fetched after the rows so the overview never waits for them.
  private async loadPeriodValues(companyId: number, fromDate: string, request: IOverViewRequestViewModel) {
    this.periodValues = new Map();
    const valuesRequest = this.portfolioReportCache.instrumentValues(companyId, fromDate, request.toDate, undefined, request.portfolio || undefined, false);
    this.periodValuesRequest = valuesRequest;
    let values: InstrumentValuesReportViewModel[];
    try {
      values = await valuesRequest;
    } catch {
      return;
    }
    if (this.periodValuesRequest !== valuesRequest) {
      return;
    }
    this.periodValues = new Map(values.map(x => [this.periodKey(x.portfolioName, x.reportingInstrumentId), x]));
    this.gridApi.refreshClientSideRowModel('filter');
    this.gridApi.refreshCells({ columns: ['periodProfitLoss', 'periodDividend', 'periodInterest'], force: true });
  }

  private periodKey(portfolio: string | undefined, instrumentId: number | undefined) {
    return `${portfolio ?? ''}_${instrumentId}`;
  }

  private periodValue(params: ValueGetterParams<InstrumentWithValuesViewModel>, select: (values: InstrumentValuesReportViewModel) => number) {
    const values = params.data && this.periodValues.get(this.periodKey(params.data.portfolio, params.data.id));
    return values ? select(values) : undefined;
  }

  periodProfitLossValueGetter = (params: ValueGetterParams<InstrumentWithValuesViewModel>) => this.periodValue(params, x => x.profitLossChangeInPeriod);

  periodDividendValueGetter = (params: ValueGetterParams<InstrumentWithValuesViewModel>) => this.periodValue(params, x => x.dividendChangeInPeriod);

  periodInterestValueGetter = (params: ValueGetterParams<InstrumentWithValuesViewModel>) => this.periodValue(params, x => x.interestChangeInPeriod);

  // A failed lookup is shown in the cells of the noted instruments instead of blocking the overview.
  private exchangeValue(params: ValueGetterParams<InstrumentWithValuesViewModel>, select: (exchange: LookupExchangeResponseItem) => string) {
    const id = params.data?.lookupAvailableInstrumentId;
    if (!id) {
      return undefined;
    }
    if (this.exchangesError) {
      return 'InstrumentLookup error';
    }
    const exchange = this.exchanges.get(id);
    return exchange ? select(exchange) : undefined;
  }

  marketValueGetter = (params: ValueGetterParams<InstrumentWithValuesViewModel>) => this.exchangeValue(params, x => x.mic);

  exchangeNameValueGetter = (params: ValueGetterParams<InstrumentWithValuesViewModel>) => this.exchangeValue(params, x => x.exchangeName);

  saveGridState() {
    this.agGridState.saveGridState(this.gridApi, this.stateKey);
  }

  saveAggregatedFields() {
    localStorage.setItem(`reporting-overview-aggregated-fields-${this.customerService.selectedCustomer.companyId}`, JSON.stringify(this.statusPanelState.fields));
  }

  typeValueFormatter = (params: ValueFormatterParams) => typeof (params.value) === 'string'
    ? this.instrumentTypes.find(x => x.id === parseInt(params.value as string))?.name
    : this.instrumentTypes.find(x => x.id === params.value)?.name;

  boolValueFormatter = (params: ValueFormatterParams) => typeof (params.value) === 'string'
    ? params.value === 'true' ? 'Yes' : 'No'
    : params.value ? 'Yes' : 'No';

  priceTypeTooltip = (params: ITooltipParams<InstrumentWithValuesViewModel, string>) => {
    switch (params.value) {
      case 'MO': return 'Manual Override';
      case 'MF': return 'Manual Fallback';
      case 'IP': return 'Imported Price';
      default: return params.value;
    }
  };
}
